import { type CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ContentContext } from 'context/ContentContext';

import { OfferBlock } from 'ui/atoms/OfferBlock/OfferBlock';
import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';

import { ModalFormBook } from 'features/articles/organisms/ModalFormBook';

import styles from './GetStarted.module.scss';

const baseClass = 'get-started';

type Props = {
  title: string;
  description: string;
};

type Dot = {
  size: number;
  x: number;
  y: number;
  color: string;
  delay: number;
  duration: number;
  rayAngle: string;
};

const TOTAL_DOTS = 22;
const RADIUS = 230;
const COLORS = ['#FF5548', '#00BB83', '#1890ff', '#FEE06C', '#8A33D6', '#69D8D6', '#FFC03A', '#00B1FD', '#A2E162'];

const pickRandomIndices = (n: number, k: number) => {
  const arr = Array.from({ length: n }, (_, i) => i);

  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return new Set(arr.slice(0, k));
};

const generateDots = (): Dot[] => {
  const largeCount = 6 + Math.floor(Math.random() * 3);
  const largeIndices = pickRandomIndices(TOTAL_DOTS, largeCount);

  return Array.from({ length: TOTAL_DOTS }, (_, i) => {
    const angle = (i * 2 * Math.PI) / TOTAL_DOTS;
    const isLarge = largeIndices.has(i);
    const size = isLarge ? 24 + Math.floor(Math.random() * 13) : 14 + Math.floor(Math.random() * 7);
    const x = Math.cos(angle) * RADIUS;
    const y = Math.sin(angle) * RADIUS;

    return {
      size,
      x,
      y,
      color: COLORS[i % COLORS.length],
      delay: Math.random() * 2.5,
      duration: 4 + Math.random() * 2,
      rayAngle: `${Math.atan2(-y, -x)}rad`,
    };
  });
};

export const GetStarted = ({ title, description }: Props) => {
  const { secondaryNavigation, pageVars } = useContext(ContentContext);

  const [lazyMounted, setLazyMounted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLazyMounted(true);
    }, 1800);
  }, []);

  const onToggleModal = useCallback(() => setShowModal((s) => !s), []);

  const dots = useMemo(() => {
    if (!lazyMounted) {
      return [];
    }

    return generateDots();
  }, [lazyMounted]);

  return (
    <>
      {showModal && <ModalFormBook title="Request a demo" maxWidth={630} onClose={onToggleModal} />}

      <section className={styles[`${baseClass}-wrapper`]}>
        <div className={styles[`${baseClass}-container`]}>
          <OfferBlock
            title={title}
            description={description}
            tools={
              <div className={styles[`${baseClass}-buttons`]}>
                <Button
                  size="xl"
                  type="dark"
                  route={secondaryNavigation.book_demo.slug}
                  className={styles[`${baseClass}-button`]}
                >
                  {pageVars.common.book_demo_button}
                </Button>

                <Button size="xl" type="dark-outline" className={styles[`${baseClass}-button`]} onClick={onToggleModal}>
                  Contact Sales
                </Button>
              </div>
            }
          />

          <div className={styles[`${baseClass}-points`]}>
            <div className={styles[`${baseClass}-logo`]}>
              <Icon type="logo-4" className={styles[`${baseClass}-icon`]} />
            </div>

            <div className={styles[`${baseClass}-dots`]}>
              {dots.map((dot, i) => {
                const rayLength = Math.hypot(dot.x, dot.y);

                return (
                  <div
                    key={i}
                    className={styles[`${baseClass}-dot`]}
                    style={
                      {
                        '--x': `${dot.x}px`,
                        '--y': `${dot.y}px`,
                        '--dot-size': `${dot.size}px`,
                        '--dot-color': dot.color,
                        '--delay': `${dot.delay}s`,
                        '--dur': `${dot.duration}s`,
                        '--ray-angle': dot.rayAngle,
                        '--ray-length': `${rayLength}px`,
                      } as CSSProperties
                    }
                  >
                    <div className={styles[`${baseClass}-ray`]} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
